// 问卷步骤4：风险因素与联系信息
// 包含诉讼仲裁、环保合规、监管审批、联系方式等

import React from 'react';
import {
  Form,
  Select,
  Radio,
  Input,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Alert,
  TimePicker,
} from 'antd';
import {
  ExclamationCircleOutlined,
  SafetyOutlined,
  FileTextOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  LitigationStatus,
  EnvironmentalCompliance,
  RegulatoryApproval,
  UrgencyLevel,
  InitialScreeningQuestionnaire,
} from '../../../types/questionnaire';
import { CONTACT_CONFIG } from '../../../config/contact';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface QuestionnaireStep4Props {
  form: any;
  questionnaire: Partial<InitialScreeningQuestionnaire>;
  onUpdate: (data: Partial<InitialScreeningQuestionnaire>) => void;
}

// 选项标签映射 - 使用翻译函数
const getLitigationStatusLabels = (t: (key: string) => string) => ({
  [LitigationStatus.NO_MAJOR_LITIGATION]: t(
    'questionnaire:options.litigationStatus.no_major_litigation'
  ),
  [LitigationStatus.MINOR_LITIGATION]: t(
    'questionnaire:options.litigationStatus.minor_litigation'
  ),
  [LitigationStatus.MAJOR_LITIGATION_HIGH_WIN_RATE]: t(
    'questionnaire:options.litigationStatus.major_litigation_high_win_rate'
  ),
  [LitigationStatus.MAJOR_LITIGATION_HIGH_LOSS_RISK]: t(
    'questionnaire:options.litigationStatus.major_litigation_high_loss_risk'
  ),
});

const getEnvironmentalComplianceLabels = (t: (key: string) => string) => ({
  [EnvironmentalCompliance.GOOD_COMPLIANCE]: t(
    'questionnaire:options.environmentalCompliance.good_compliance'
  ),
  [EnvironmentalCompliance.MINOR_ISSUES_RESOLVED]: t(
    'questionnaire:options.environmentalCompliance.minor_issues_resolved'
  ),
  [EnvironmentalCompliance.VIOLATIONS_BEING_HANDLED]: t(
    'questionnaire:options.environmentalCompliance.violations_being_handled'
  ),
  [EnvironmentalCompliance.MAJOR_VIOLATION_RISK]: t(
    'questionnaire:options.environmentalCompliance.major_violation_risk'
  ),
});

const getRegulatoryApprovalLabels = (t: (key: string) => string) => ({
  [RegulatoryApproval.ALL_APPROVALS_OBTAINED]: t(
    'questionnaire:options.regulatoryApproval.all_approvals_obtained'
  ),
  [RegulatoryApproval.MOST_APPROVALS_OBTAINED]: t(
    'questionnaire:options.regulatoryApproval.most_approvals_obtained'
  ),
  [RegulatoryApproval.APPLYING_KEY_APPROVALS]: t(
    'questionnaire:options.regulatoryApproval.applying_key_approvals'
  ),
  [RegulatoryApproval.NOT_STARTED]: t(
    'questionnaire:options.regulatoryApproval.not_started'
  ),
});

const getUrgencyLevelLabels = (t: (key: string) => string) => ({
  [UrgencyLevel.VERY_URGENT]: t(
    'questionnaire:options.urgencyLevel.very_urgent'
  ),
  [UrgencyLevel.URGENT]: t('questionnaire:options.urgencyLevel.urgent'),
  [UrgencyLevel.NORMAL]: t('questionnaire:options.urgencyLevel.normal'),
  [UrgencyLevel.NOT_URGENT]: t('questionnaire:options.urgencyLevel.not_urgent'),
});

// 风险评估函数
const getRiskLevel = (
  litigationStatus?: string,
  envCompliance?: string,
  regApproval?: string
) => {
  const highRiskConditions = [
    LitigationStatus.MAJOR_LITIGATION_HIGH_LOSS_RISK,
    EnvironmentalCompliance.MAJOR_VIOLATION_RISK,
  ];

  const mediumRiskConditions = [
    LitigationStatus.MAJOR_LITIGATION_HIGH_WIN_RATE,
    EnvironmentalCompliance.VIOLATIONS_BEING_HANDLED,
    RegulatoryApproval.NOT_STARTED,
  ];

  if (
    (litigationStatus &&
      highRiskConditions.includes(litigationStatus as any)) ||
    (envCompliance && highRiskConditions.includes(envCompliance as any))
  ) {
    return 'high';
  }

  if (
    (litigationStatus &&
      mediumRiskConditions.includes(litigationStatus as any)) ||
    (envCompliance && mediumRiskConditions.includes(envCompliance as any)) ||
    (regApproval && mediumRiskConditions.includes(regApproval as any))
  ) {
    return 'medium';
  }

  return 'low';
};

export const QuestionnaireStep4: React.FC<QuestionnaireStep4Props> = ({
  form,
  questionnaire,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const watchedValues = Form.useWatch([], form) as any;

  // 动态风险评估
  const currentRiskLevel = getRiskLevel(
    watchedValues?.riskFactors?.majorLitigation,
    watchedValues?.riskFactors?.environmentalCompliance,
    watchedValues?.riskFactors?.regulatoryApproval
  );

  const getRiskAlert = () => {
    switch (currentRiskLevel) {
      case 'high':
        return {
          type: 'error' as const,
          message: t('questionnaire:step4.riskHigh'),
          description: t('questionnaire:step4.riskHighDesc'),
        };
      case 'medium':
        return {
          type: 'warning' as const,
          message: t('questionnaire:step4.riskMedium'),
          description: t('questionnaire:step4.riskMediumDesc'),
        };
      default:
        return {
          type: 'success' as const,
          message: t('questionnaire:step4.riskLow'),
          description: t('questionnaire:step4.riskLowDesc'),
        };
    }
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      {/* 风险评估说明 */}
      <Alert
        message={t('questionnaire:step4.riskDesc')}
        description={t('questionnaire:step4.riskDescContent')}
        type="info"
        showIcon
        style={{
          borderRadius: '8px',
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.05)',
        }}
      />

      {/* D1. 诉讼仲裁状况 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <ExclamationCircleOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step4.litigationSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Form.Item
          name={['riskFactors', 'majorLitigation']}
          label={t('questionnaire:step4.majorLitigation')}
          rules={[
            {
              required: true,
              message: t('questionnaire:step4.majorLitigationRequired'),
            },
          ]}
          extra={t('questionnaire:step4.majorLitigationExtra')}
        >
          <Radio.Group style={{ width: '100%' }}>
            <Row gutter={[16, 12]}>
              {Object.entries(getLitigationStatusLabels(t)).map(
                ([value, label]) => (
                  <Col xs={24} sm={12} key={value}>
                    <Radio
                      value={value}
                      style={{ width: '100%', padding: '8px' }}
                    >
                      {label}
                    </Radio>
                  </Col>
                )
              )}
            </Row>
          </Radio.Group>
        </Form.Item>
      </Card>

      {/* D2. 环保合规状况 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <SafetyOutlined style={{ color: '#10b981' }} />
              <Text strong>
                {t('questionnaire:step4.envComplianceSection')}
              </Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Form.Item
          name={['riskFactors', 'environmentalCompliance']}
          label={t('questionnaire:step4.environmentalCompliance')}
          rules={[
            {
              required: true,
              message: t('questionnaire:step4.environmentalComplianceRequired'),
            },
          ]}
          extra={t('questionnaire:step4.environmentalComplianceExtra')}
        >
          <Radio.Group style={{ width: '100%' }}>
            <Row gutter={[16, 12]}>
              {Object.entries(getEnvironmentalComplianceLabels(t)).map(
                ([value, label]) => (
                  <Col xs={24} key={value}>
                    <Radio
                      value={value}
                      style={{ width: '100%', padding: '8px' }}
                    >
                      {label}
                    </Radio>
                  </Col>
                )
              )}
            </Row>
          </Radio.Group>
        </Form.Item>
      </Card>

      {/* D3. 监管审批状态 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <FileTextOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step4.regulatorySection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Form.Item
          name={['riskFactors', 'regulatoryApproval']}
          label={t('questionnaire:step4.regulatoryApproval')}
          rules={[
            {
              required: true,
              message: t('questionnaire:step4.regulatoryApprovalRequired'),
            },
          ]}
          extra={t('questionnaire:step4.regulatoryApprovalExtra')}
        >
          <Select
            placeholder={t('questionnaire:step4.regulatoryApprovalPlaceholder')}
          >
            {Object.entries(getRegulatoryApprovalLabels(t)).map(
              ([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              )
            )}
          </Select>
        </Form.Item>
      </Card>

      {/* 动态风险评估结果 */}
      {(watchedValues?.riskFactors?.majorLitigation ||
        watchedValues?.riskFactors?.environmentalCompliance ||
        watchedValues?.riskFactors?.regulatoryApproval) && (
        <Alert {...getRiskAlert()} showIcon style={{ borderRadius: '8px' }} />
      )}

      {/* D4. 联系信息 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <UserOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step4.contactSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={['contactInfo', 'contactName']}
              label={t('questionnaire:step4.contactName')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step4.contactNameRequired'),
                },
              ]}
            >
              <Input
                placeholder={t('questionnaire:step4.contactNamePlaceholder')}
                prefix={<UserOutlined style={{ color: '#10b981' }} />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['contactInfo', 'position']}
              label={t('questionnaire:step4.position')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step4.positionRequired'),
                },
              ]}
            >
              <Input
                placeholder={t('questionnaire:step4.positionPlaceholder')}
                prefix={<UserOutlined style={{ color: '#10b981' }} />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['contactInfo', 'phone']}
              label={t('questionnaire:step4.phone')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step4.phoneRequired'),
                },
                { len: 11, message: t('questionnaire:step4.phoneLength') },
                {
                  pattern: /^1[3-9]\d{9}$/,
                  message: t('questionnaire:step4.phoneFormat'),
                },
              ]}
            >
              <Input
                placeholder={t('questionnaire:step4.phonePlaceholder')}
                prefix={<PhoneOutlined style={{ color: '#10b981' }} />}
                maxLength={11}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['contactInfo', 'email']}
              label={t('questionnaire:step4.email')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step4.emailRequired'),
                },
                {
                  type: 'email',
                  message: t('questionnaire:step4.emailFormat'),
                },
              ]}
            >
              <Input
                placeholder={t('questionnaire:step4.emailPlaceholder')}
                prefix={<MailOutlined style={{ color: '#10b981' }} />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['contactInfo', 'urgencyLevel']}
              label={t('questionnaire:step4.urgencyLevel')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step4.urgencyLevelRequired'),
                },
              ]}
            >
              <Select
                placeholder={t('questionnaire:step4.urgencyLevelPlaceholder')}
                prefix={<ClockCircleOutlined style={{ color: '#10b981' }} />}
              >
                {Object.entries(getUrgencyLevelLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 补充说明（可选） */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <FileTextOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step4.additionalSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Form.Item
          name="additionalInfo"
          label={t('questionnaire:step4.additionalInfo')}
          style={{ marginBottom: '0' }}
        >
          <TextArea
            rows={8}
            placeholder={t('questionnaire:step4.additionalInfoPlaceholder')}
            maxLength={500}
            style={{
              width: '100%',
              minHeight: '200px',
              fontSize: '16px',
              lineHeight: '1.6',
              padding: '12px',
              boxSizing: 'border-box',
              display: 'block',
            }}
          />
        </Form.Item>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginTop: '8px',
            marginBottom: '16px',
            gap: '12px',
          }}
        >
          <span
            style={{
              fontSize: '12px',
              color: '#6b7280',
              lineHeight: '1.5',
              flex: 1,
            }}
          >
            {t('questionnaire:step4.additionalInfoHint')}
          </span>
          <span
            style={{
              fontSize: '12px',
              color: '#999',
              whiteSpace: 'nowrap',
            }}
          >
            {form.getFieldValue('additionalInfo')?.length || 0}/500
          </span>
        </div>
      </Card>

      {/* 服务承诺 */}
      <Card
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Space>
          <SafetyOutlined style={{ color: '#10b981' }} />
          <div>
            <Text style={{ color: '#10b981' }}>
              <strong>{t('questionnaire:step4.servicePromise')}</strong>
            </Text>
            <div style={{ marginTop: '4px', color: '#6b7280' }}>
              • {t('questionnaire:step4.serviceTip1')}
              <br />• {t('questionnaire:step4.serviceTip2')}
              <br />• {t('questionnaire:step4.serviceTip3')}
              <br />• {t('questionnaire:step4.serviceTip4')}
              {CONTACT_CONFIG.serviceHotline}
            </div>
          </div>
        </Space>
      </Card>
    </Space>
  );
};
